<script setup>
// Aba Etiquetas da caixa (v77): escolhe as etiquetas e os grupos que ESTA
// caixa pode usar na interface (picker da conversa, acoes em massa etc.).
// Nada marcado = todas liberadas (padrao). Trava so de interface —
// automacoes/CLT-IA continuam aplicando qualquer etiqueta.
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LabelGroupsAPI from 'dashboard/api/labelGroups';
import { useInboxAllowedLabels } from 'dashboard/composables/useInboxAllowedLabels';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const getters = useStoreGetters();
const { refreshInboxLabelLinks } = useInboxAllowedLabels();

const allLabels = computed(() => getters['labels/getLabels'].value);
const groups = ref([]);
const selectedLabelIds = ref({});
const selectedGroupIds = ref({});
const isLoading = ref(true);
const isSaving = ref(false);

const selectedCount = computed(
  () =>
    Object.values(selectedLabelIds.value).filter(Boolean).length +
    Object.values(selectedGroupIds.value).filter(Boolean).length
);

// Previa do efeito: titulos liberados (diretos + grupos marcados)
const effectiveTitles = computed(() => {
  const ids = new Set(
    Object.keys(selectedLabelIds.value)
      .filter(id => selectedLabelIds.value[id])
      .map(Number)
  );
  groups.value.forEach(g => {
    if (selectedGroupIds.value[g.id]) g.label_ids.forEach(id => ids.add(id));
  });
  return allLabels.value
    .filter(l => ids.has(l.id))
    .map(l => l.title);
});

const load = async () => {
  isLoading.value = true;
  try {
    const [groupsRes, configRes] = await Promise.all([
      LabelGroupsAPI.list(),
      LabelGroupsAPI.getInboxConfig(props.inbox.id),
    ]);
    groups.value = groupsRes.data.data || [];
    selectedLabelIds.value = {};
    selectedGroupIds.value = {};
    (configRes.data.label_ids || []).forEach(id => {
      selectedLabelIds.value[id] = true;
    });
    (configRes.data.label_group_ids || []).forEach(id => {
      selectedGroupIds.value[id] = true;
    });
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    await LabelGroupsAPI.setInboxConfig(props.inbox.id, {
      labelIds: Object.keys(selectedLabelIds.value)
        .filter(id => selectedLabelIds.value[id])
        .map(Number),
      labelGroupIds: Object.keys(selectedGroupIds.value)
        .filter(id => selectedGroupIds.value[id])
        .map(Number),
    });
    await refreshInboxLabelLinks();
    useAlert(
      selectedCount.value
        ? 'Etiquetas da caixa salvas — só as selecionadas ficam disponíveis nas conversas desta caixa.'
        : 'Nenhuma seleção — esta caixa volta a usar TODAS as etiquetas.'
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="mx-6 flex flex-col gap-6 max-w-4xl">
    <div>
      <h2 class="text-heading-2 text-n-slate-12 mb-1">Etiquetas da caixa</h2>
      <p class="text-n-slate-11 text-sm">
        Escolha as etiquetas e/ou grupos que esta caixa pode usar. Com algo
        selecionado, as conversas desta caixa só mostram e atribuem essas
        etiquetas. Sem nada selecionado, todas as etiquetas ficam liberadas
        (padrão). Automações e robôs não são afetados.
      </p>
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-11 py-6">
      Carregando…
    </div>
    <template v-else>
      <div class="rounded-lg border border-n-weak p-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">Etiquetas</p>
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-1 max-h-80 overflow-y-auto">
          <label
            v-for="label in allLabels"
            :key="label.id"
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer py-0.5"
          >
            <input
              v-model="selectedLabelIds[label.id]"
              type="checkbox"
              class="accent-current"
            />
            <span
              class="inline-block w-2.5 h-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="truncate">{{ label.title }}</span>
          </label>
        </div>
      </div>

      <div class="rounded-lg border border-n-weak p-4">
        <p class="text-sm font-medium text-n-slate-12 mb-2">
          Grupos de etiquetas
        </p>
        <p v-if="!groups.length" class="text-xs text-n-slate-10">
          Nenhum grupo criado ainda — crie em Configurações → Etiquetas →
          GRUPO DE ETIQUETAS.
        </p>
        <div v-else class="grid grid-cols-2 lg:grid-cols-3 gap-1">
          <label
            v-for="g in groups"
            :key="g.id"
            class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer py-0.5"
          >
            <input
              v-model="selectedGroupIds[g.id]"
              type="checkbox"
              class="accent-current"
            />
            <span class="truncate">{{ g.name }}</span>
            <span class="text-xs text-n-slate-10">
              ({{ g.label_ids.length }})
            </span>
          </label>
        </div>
      </div>

      <div class="rounded-lg border border-n-weak p-4">
        <p class="text-sm font-medium text-n-slate-12 mb-1">
          Efeito nas conversas desta caixa:
        </p>
        <p v-if="!selectedCount" class="text-sm text-n-slate-11">
          TODAS as etiquetas disponíveis (nenhuma restrição).
        </p>
        <p v-else class="text-sm text-n-slate-11 break-words">
          Somente: {{ effectiveTitles.join(', ') || '(nenhuma)' }}
        </p>
      </div>

      <div class="flex justify-end">
        <NextButton label="Salvar" :is-loading="isSaving" @click="save" />
      </div>
    </template>
  </div>
</template>
